'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * FilterChip — toggleable filter pill.
 * Figma: Design System / Compositions / FilterChip (242:1032).
 *
 * Three states: Idle (resting), Selected (active filter), Disabled.
 * Optional trailing count badge for results-per-filter readouts.
 */

const chipVariants = cva(
    [
        'inline-flex items-center gap-2 h-8 px-3 rounded-full border text-sm font-medium leading-5',
        // 32px visual pill; pseudo-element pads the hit area to 44px (FacetedFilter idiom)
        "relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
        'transition-colors duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2',
    ],
    {
        variants: {
            state: {
                idle:
                    'bg-[var(--bg-surface)] border-[var(--border-default)] text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]',
                selected:
                    // sage, matching the segmented pill tracks (filter role);
                    // text-safe sage only — sage-500 as text fails AA
                    'bg-[var(--sage-50)] border-[var(--sage-300)] text-[var(--text-sage)] hover:bg-[var(--sage-100)]',
                disabled:
                    'bg-[var(--bg-subtle)] border-[var(--border-subtle)] text-[var(--text-disabled)] cursor-not-allowed',
            },
        },
        defaultVariants: { state: 'idle' },
    },
)

const countVariants = cva(
    'inline-flex items-center justify-center min-w-5 h-4.5 px-1.5 rounded-full text-xs font-medium leading-4',
    {
        variants: {
            state: {
                idle: 'bg-[var(--bg-subtle)] text-[var(--text-secondary)]',
                // sage-600 fill, not 500 — white-on-500 is 3.95:1, sub-AA at text-xs
                selected: 'bg-[var(--sage-600)] text-[var(--text-on-brand)]',
                disabled: 'bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-[var(--text-disabled)]',
            },
        },
        defaultVariants: { state: 'idle' },
    },
)

export interface FilterChipProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
        VariantProps<typeof chipVariants> {
    selected?: boolean
    count?: number | string
    children: React.ReactNode
}

export const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
    ({ className, selected, count, disabled, children, ...props }, ref) => {
        const state = disabled ? 'disabled' : selected ? 'selected' : 'idle'
        return (
            <button
                ref={ref}
                type="button"
                aria-pressed={selected ?? false}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                className={cn(chipVariants({ state }), className)}
                {...props}
            >
                <span className="truncate">{children}</span>
                {count !== undefined && count !== null && (
                    <span className={countVariants({ state })}>{count}</span>
                )}
            </button>
        )
    },
)
FilterChip.displayName = 'FilterChip'
